import { AppDispatch, RootState } from '../../redux/store/store';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { useDispatch, useSelector } from 'react-redux';
import './index.css';

import Loader from '../common/Loader';
import { RTU } from '../../types/RTU';
import RTUFilter from './RTUFilter';
import RTUList from './RTUList';
import RTUMap from './RTUMap';
import { fetchRTUs } from '../../redux/slices/rtuSlice';
import { currentFleet } from '../RtuCluster/fleet';
import { EMPTY_CRITERIA, RtuFilterCriteria, filterRtus, isCriteriaActive } from './filterRtus';

// import RTUMap from './RTUMap';



// const RTUMap = React.lazy(() => import('./RTUMap'));

const ignoreSearchResult = (_rtus: RTU[]) => undefined;

const MapViewPage: React.FC = () => {
  const { rtus: allRtus } = useSelector((state: RootState) => state.rtu || {});
  // The map, its list and the total all work on the current fleet: each device once, standalone former records out.
  const rtus = useMemo(() => currentFleet(allRtus || []).rows, [allRtus]);
  const [, setSearchQuery] = useState<string>('');
  const [currentRTU, setCurrentRTU] = useState<RTU | undefined>();
  const [hoveredRTU, setHoveredRTU] = useState<RTU | null>(null);
  const [criteria, setCriteria] = useState<RtuFilterCriteria>(EMPTY_CRITERIA);
  const [dataLoaded, setDataLoaded] = useState<boolean>(false)
  const [rtuIdsInBounds, setRtuIdsInBounds] = useState<number[]>([]);
  const [mapBoundsChanged, setMapBoundsChanged] = useState<boolean>(false);


  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // When component mounts, fetch data
    dispatch(fetchRTUs({})).then(() => {
      // Set state to indicate data has been loaded
      setDataLoaded(true);
    });
  }, [dispatch]);

  const rtuSearchResult = useMemo(() => filterRtus(rtus, criteria), [rtus, criteria]);
  const hasAppliedFilter = useMemo(() => isCriteriaActive(criteria), [criteria]);

  const setFilters = useCallback((newFilters: RtuFilterCriteria) => {
    setCriteria({ ...newFilters });
    setMapBoundsChanged(false);
  }, []);

  const clearFilters = useCallback(() => {
    setCriteria(EMPTY_CRITERIA);
    setMapBoundsChanged(false);
  }, []);

  // Distinct, sorted Location / Producer / Field values from the fleet, for filter suggestions.
  const locationOptions = useMemo(
    () => Array.from(new Set((rtus || []).map((r) => r.location).filter(Boolean) as string[])).sort(),
    [rtus]
  );
  const producerOptions = useMemo(
    () => Array.from(new Set((rtus || []).map((r) => r.provider).filter(Boolean) as string[])).sort(),
    [rtus]
  );
  const fieldOptions = useMemo(
    () => Array.from(new Set((rtus || []).map((r) => r.field).filter(Boolean) as string[])).sort(),
    [rtus]
  );


  useEffect(() => {
    const stopAnimation = (event: MouseEvent) => {
      // Check if the clicked element is an RTU card
      if (!(event.target as HTMLElement).closest('.rtu-card')) {
        setCurrentRTU(undefined);
      }
    };
  
    document.addEventListener('click', stopAnimation);
  
    return () => {
      document.removeEventListener('click', stopAnimation);
    };
  }, []);



  
  const findRTUsInBounds = useCallback((ne: any, sw: any) => {
    const hasValidBounds =
      Number.isFinite(ne?.lat) &&
      Number.isFinite(ne?.lng) &&
      Number.isFinite(sw?.lat) &&
      Number.isFinite(sw?.lng);

    if (!hasValidBounds) {
      setMapBoundsChanged(false);
      return;
    }

    const sourceRtus = rtuSearchResult;
    if (!sourceRtus || sourceRtus.length === 0) {
      setRtuIdsInBounds([]);
      setMapBoundsChanged(false);
      return;
    }

    const rtusWithinBounds = sourceRtus.filter((rtu) => {
      if (!Number.isFinite(rtu.latitude) || !Number.isFinite(rtu.longitude)) {
        return false;
      }

      const inLatitudeRange = rtu.latitude >= sw.lat && rtu.latitude <= ne.lat;
      if (!inLatitudeRange) {
        return false;
      }

      // Handles standard ranges and anti-meridian wrapping (sw.lng > ne.lng).
      const inLongitudeRange =
        sw.lng <= ne.lng
          ? rtu.longitude >= sw.lng && rtu.longitude <= ne.lng
          : rtu.longitude >= sw.lng || rtu.longitude <= ne.lng;

      return inLongitudeRange;
    });

    setRtuIdsInBounds(rtusWithinBounds.map((rtu) => rtu.id));
    setMapBoundsChanged(rtusWithinBounds.length > 0);
    setSearchQuery(`lat between ${sw.lat} and ${ne.lat} AND lon between ${sw.lng} and ${ne.lng}`);
  }, [rtuSearchResult, setSearchQuery]);

  const visibleRtus = useMemo(() => {
    if (!mapBoundsChanged || rtuIdsInBounds.length === 0) {
      return rtuSearchResult;
    }
    const idsInBounds = new Set(rtuIdsInBounds);
    return rtuSearchResult.filter((rtu) => idsInBounds.has(rtu.id));
  }, [mapBoundsChanged, rtuIdsInBounds, rtuSearchResult]);


  return (
    <div className="map-view" style={{ height: window.innerHeight - 50, padding: '0.25rem' }}>
      <Splitter>
        <SplitterPanel size={15}>
          <RTUFilter setFilters={setFilters} clearFilters={clearFilters} locationOptions={locationOptions} producerOptions={producerOptions} fieldOptions={fieldOptions} />
        </SplitterPanel>
        <SplitterPanel size={25}>
          <RTUList
            rtus={visibleRtus}
            currentRTU={currentRTU}
            setCurrentRTU={setCurrentRTU}
            setHoveredRTU={setHoveredRTU}
            totalCount={rtus.length}
            hasAppliedFilter={hasAppliedFilter}
          />
        </SplitterPanel>
        <SplitterPanel size={60}>
          {dataLoaded ? (
            <RTUMap 
              rtus={rtuSearchResult} 
              setSearchQuery={setSearchQuery} 
              currentRTU={currentRTU} 
              setRtuSearchResult={ignoreSearchResult}
              hoveredRTU={hoveredRTU} 
              findRTUsInBounds={findRTUsInBounds}
            />
          ) : (
            <Loader message='Getting devices data ...' />
          )}
        </SplitterPanel>
      </Splitter>
    </div>
  );
};

export default MapViewPage;
